import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { CustomViewOfferPriceList } from "./pages/custom/viewOfferPriceList";

const DevelopmentIndex = lazy(() => import("./pages/development"));

const Desktop = lazy(() => import("./pages/desktop"));
const TaskList = lazy(() => import("./pages/task-list"));
const Kanban = lazy(() => import("./pages/kanban"));
const Gantt = lazy(() => import("./pages/gantt"));
const FileRepository = lazy(() => import("./pages/file-repository"));
const CompanyPage = lazy(() => import("./pages/company"));
const CompanyList = lazy(() => import("./pages/company-list"));
const Commission = lazy(() => import("./pages/commission"));
const CommissionList = lazy(() => import("./pages/commission-list"));
const CampaignOpportunityList = lazy(
  () => import("./pages/campaign-opportunity-list")
);
const DocumentInvoiceList = lazy(() => import("./pages/document-invoice-list"));
const DocumentOrderList = lazy(() => import("./pages/document-order-list"));
const DocumentOfferList = lazy(() => import("./pages/document-offer-list"));
const DocumentOfferQuestion = lazy(
  () => import("./pages/document-offer-question")
);
const Timeline = lazy(() => import("./pages/timeline"));
const CommissionCalendar = lazy(() => import("./pages/commission-calendar"));
const GanttConfig = lazy(() => import("./pages/gantt-config"));
const KanbanConfig = lazy(() => import("./pages/kanban-config"));
const Calendar = lazy(() => import("./pages/calendar"));
const EmailMessage = lazy(() => import("./pages/email-message"));
const ServicedProduct = lazy(() => import("./pages/serviced-product"));
const ServicedProductList = lazy(() => import("./pages/serviced-product-list"));
const TargetConfig = lazy(() => import("./pages/target"));
const CustomSettingDocumentOfferQuestion = lazy(
  () => import("./pages/custom/setting-document-offer-question")
);

function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {process.env.NODE_ENV === "development" && (
          <Route path="js/react" element={<DevelopmentIndex />} />
        )}
        <Route
          path="/customViewOfferPriceList.html"
          element={<CustomViewOfferPriceList />}
        ></Route>
        <Route
          path="/customSettingDocumentOfferQuestion.html"
          element={<CustomSettingDocumentOfferQuestion />}
        ></Route>
        <Route path="/">
          <Route index element={<Desktop />} />
          <Route path="info" element={<Desktop />} />
          <Route path="info/home" element={<Desktop />} />
          <Route
            path="info/editAccountUserDesktop/user_profile_id/:id"
            element={<Desktop />}
          />
        </Route>
        <Route path="commission">
          <Route index element={<Navigate to="list" />}></Route>
          <Route
            path="listForCompany/list_company_id/:listCompanyId"
            element={<CommissionList />}
          />
          <Route path="list/company_id/:id" element={<CommissionList />} />
          <Route path="list" element={<CommissionList />} />
          <Route path="show/commission_id/:id" element={<Commission />} />
          <Route path="calendar" element={<CommissionCalendar />} />
          <Route path="timeline" element={<Timeline />} />
        </Route>
        <Route path="kanban">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<KanbanConfig />}>
            <Route path="page/:pageNumber" element={<KanbanConfig />} />
          </Route>
          <Route path="show/id/:id" element={<Kanban />} />
        </Route>
        <Route path="gantt">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<GanttConfig />}>
            <Route path="page/:pageNumber" element={<GanttConfig />} />
          </Route>
          <Route path="show/id/:id" element={<Gantt />} />
        </Route>
        <Route path="userCalendar" element={<Calendar />} />
        <Route path="emailMessage/mailFolder" element={<EmailMessage />} />
        <Route
          path="fileDirectory/fileRepository"
          element={<FileRepository />}
        />
        <Route path="servicedProduct">
          <Route index element={<Navigate to="list" />}></Route>
          <Route
            path="listForAll/company_id/:companyId"
            element={<ServicedProductList />}
          />
          <Route
            path="show/serviced_product_id/:id"
            element={<ServicedProduct />}
          />
          <Route
            path="edit/serviced_product_id/:id"
            element={<ServicedProduct />}
          />
          <Route path="listForAll" element={<ServicedProductList />} />
          <Route path="list" element={<ServicedProductList />} />
        </Route>
        <Route path="target">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<TargetConfig />}>
            <Route path="page/:pageNumber" element={<TargetConfig />} />
          </Route>
        </Route>
        {/* Listy */}
        <Route path="task">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<TaskList />} />
          <Route
            path="listForCompany/task_list_company_id/:id/filter/true/task_category/:type"
            element={<TaskList />}
          />
        </Route>
        <Route path="campaignOpportunity">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<CampaignOpportunityList />} />
          <Route
            path="list/company_id/:id"
            element={<CampaignOpportunityList />}
          />
        </Route>
        <Route path="accountCompany">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<CompanyList />} />
          <Route path="show/company_id/:id" element={<CompanyPage />} />
          <Route path="edit/company_id/:id" element={<CompanyPage />} />
        </Route>
        <Route path="documentOrder">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentOrderList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId"
              element={<DocumentOrderList />}
            />
          </Route>
          <Route
            path="/documentOrder/listForCompany/company_id/:listCompanyId"
            element={<DocumentOrderList />}
          />
          <Route
            path="/documentOrder/listForCompany"
            element={<DocumentOrderList />}
          />
        </Route>
        <Route path="documentOfferQuestion">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentOfferList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId/document_subtype/:listDocumentSubtype"
              element={<DocumentOfferList />}
            />
          </Route>
          <Route
            path="/documentOfferQuestion/list/document_subtype?/:listDocumentSubtype"
            element={<DocumentOfferList />}
          />
          <Route
            path="/documentOfferQuestion/listForCompany/company_id/:listCompanyId/document_subtype/:listDocumentSubtype"
            element={<DocumentOfferList />}
          />
          <Route
            path="/documentOfferQuestion/createByPh/commission_id?/:commissionId?/opportunity_id?/:opportunityId?/company_id?/:companyId?/document_offer_question_subtype?/:documentSubtype?"
            element={<DocumentOfferQuestion initDocumentState="create" />}
          />
          <Route
            path="/documentOfferQuestion/listProcess/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="show" />}
          />
          <Route
            path="/documentOfferQuestion/show/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="show" />}
          />
          <Route
            path="/documentOfferQuestion/editDocument/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="edit" />}
          />
          <Route
            path="/documentOfferQuestion/createByPhFromDocument/from_document_id/:id/company_id/:companyId/document_offer_question_subtype/OH/positions_from_document/:positionsIds"
            element={<DocumentOfferQuestion initDocumentState="create" />}
          />
        </Route>
        <Route path="documentInvoice">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentInvoiceList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId"
              element={<DocumentInvoiceList />}
            />
          </Route>
          <Route
            path="listPurchase"
            element={<DocumentInvoiceList documentType="2" />}
          />
          <Route
            path="listForCompany/document_type/:listDocumentType/company_id/:listCompanyId"
            element={<DocumentInvoiceList />}
          />
          <Route
            path="listForCompany/company_id/:listCompanyId"
            element={<DocumentInvoiceList />}
          />
          <Route path="listForCompany" element={<DocumentInvoiceList />} />
          <Route
            path="listUnaccounted/"
            element={<DocumentInvoiceList listUnaccounted={true} />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
